import { CONFIG } from "../../../../config";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

// Development logging utility
const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
  if (isDevelopment) {
    console.log(message, data);
  }
};

/**
 * Custom hook for exporting agent analytics to XLSX
 * 
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Object} Mutation object with download function
 */
export const useExportAgentAnalytics = (options = {}) => {
  const {
    onSuccess = () => { },
    onError = () => { },
  } = options;

  // Download function that creates blob and triggers download
  const downloadFile = useCallback((blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      devLog('[useExportAgentAnalytics] File downloaded successfully:', filename);
    } catch (error) {
      console.error('[useExportAgentAnalytics] Download failed:', error);
      throw new Error('Failed to download file');
    }
  }, []);

  // Export function
  const exportAgentAnalytics = useCallback(async (params = {}) => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useExportAgentAnalytics] Making export request with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/exports/agent-analytics`,
        {
          params: cleanParams,
          responseType: 'blob', // Important for file downloads
          timeout: 60000, // 60 second timeout for exports
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
      );

      devLog('[useExportAgentAnalytics] Response received:', {
        status: response.status,
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length'],
        dataType: typeof response.data,
        dataSize: response.data?.size || 'unknown'
      });

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'agent-analytics-export.xlsx';

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Ensure we have a blob
      let blob;
      if (response.data instanceof Blob) {
        blob = response.data;
      } else {
        // If it's not a blob, create one
        blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }

      devLog('[useExportAgentAnalytics] Blob created:', {
        size: blob.size,
        type: blob.type
      });

      downloadFile(blob, filename);

      devLog('[useExportAgentAnalytics] Export completed successfully');

      return { success: true, filename };
    } catch (error) {
      // Enhanced error handling
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error exporting agent analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Export timeout - file may be too large';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;

        // Try to read error message from blob if it's a server error
        if (error.response.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            if (errorData.message) {
              errorMessage += ` - ${errorData.message}`;
            }
          } catch (parseError) {
            // If we can't parse the error, use the original message
            devLog('[useExportAgentAnalytics] Could not parse error response:', parseError);
          }
        }
      }

      console.error(errorMessage, {
        originalError: error,
        params,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  }, [downloadFile]);

  return useMutation({
    mutationFn: exportAgentAnalytics,
    onSuccess: (data, variables) => {
      devLog('[useExportAgentAnalytics] Export mutation succeeded:', data);
      onSuccess(data, variables);
    },
    onError: (error, variables) => {
      console.error("Error in useExportAgentAnalytics mutation:", {
        error: error.message,
        params: variables,
        timestamp: new Date().toISOString()
      });
      onError(error, variables);
    },
  });
};
